import { useQuery } from '@apollo/client';

import store from 'js/lib/coursera.store';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { get as getUser } from 'js/lib/user';

import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { useAuditUpgradeFlowData } from 'bundles/audit/contexts/AuditUpgradeFlowDataContext';
import localStorageEx from 'bundles/common/utils/localStorageEx';
import { useCourseNavigationContext } from 'bundles/course-v2/providers/CourseNavigationProvider';
import GetCoachAccessEligibility from 'bundles/enroll-common/api/GetCoachAccessEligibilityQuery.graphql';
import type {
  GetCoachAccessEligibilityQuery,
  GetCoachAccessEligibilityQueryVariables,
} from 'bundles/enroll-common/api/__generated__/GetCoachAccessEligibilityQuery';
import { ENROLL_COURSE } from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { useEnrollment } from 'bundles/enroll-common/components/xdp/withEnrollment';
// eslint-disable-next-line import/no-cycle
import { useHasCompletedFirstModule } from 'bundles/enroll-common/hooks/useHasCompletedFirstModule';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import useCourseOwnership from 'bundles/entitlements-common/hooks/useCourseOwnership';
import useProductOwnerships from 'bundles/entitlements-common/hooks/useProductOwnerships';
import googleCertificateExperiments from 'bundles/epic/clients/GoogleCertificate';
import { useNaptime } from 'bundles/naptimejs';
import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import type CourseTypeMetadataV1 from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import CourseTypeMetadataV1Resource, { CourseType } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import OpenCourseMembershipsV1 from 'bundles/naptimejs/resources/openCourseMemberships.v1';
import { VERIFIED_CERTIFICATE } from 'bundles/payments-common/common/ProductType';
import useUserProductTransaction from 'bundles/payments-common/hooks/useUserProductTransaction';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';

export type UseShouldShowFreemiumUpgradeSectionParams = {
  courseId: string;
  isStandaloneCourse: boolean;
  s12nId?: string;
};

// courseId should always be passed in for critical, logic dependent parts
// For design updates, courseId does not need to be passed in so that this value is true if the Epic is enabled
export const isFreemiumModelRolloutGAEnabled = (courseId?: string) => {
  return googleCertificateExperiments.preview('isFreemiumModelRolloutGAEnabled', {
    // eslint-disable-next-line camelcase
    course_id: courseId || 'always-show',
  });
};

// courseId should always be passed in when using this function.
// For minor updates that do not depend on course exclusions, use isFreemiumModelRolloutGAEnabled instead.
export const isFreemiumModelEnabled = (courseId?: string) => {
  if (!courseId && !isFreemiumModelRolloutGAEnabled(courseId)) {
    return false;
  }

  return isFreemiumModelRolloutGAEnabled(courseId);
};

export const isEnrolledThroughFreemiumAccess = (membership?: OpenCourseMembershipsV1) => {
  if (!membership) {
    return false;
  }

  return isFreemiumModelEnabled(membership.courseId) && membership.isFreemiumLearner;
};

/**
 * Determines whether to show the Freemium upgrade section on the course completed card.
 * Returns true for the following conditions:
 * 1. Learner has completed a standalone course with payment and does not have ownership
 *    - This could happen if the learner completes a single module standalone course through Freemium
 *    - or if the learner purchases and completes a standaloone course, then the ownership lapses
 * 2. Learner has completed a multi-module course-in-s12n and does not have ownership
 *    - This could happen if the learner subscribes and completes a multi-module course-in-s12n, then subscription ends without payment
 *    - (single-module course-in-s12n completed through Freemium is handled in SingleCourseCompletionUpgradeSection.tsx)
 */
export const useShouldShowFreemiumUpgradeSection = ({
  courseId,
  isStandaloneCourse,
  s12nId,
}: UseShouldShowFreemiumUpgradeSectionParams): boolean => {
  const { numberOfModulesInCourse } = useHasCompletedFirstModule({
    courseId: courseId || '',
    shouldReturnNumberOfModules: true,
  });
  const auditUpgradeFlowContextData = useAuditUpgradeFlowData();
  const { hasCompletedCourse, isFreemiumLearner } = auditUpgradeFlowContextData || {};
  const { courseOwnership } = useProductOwnerships({
    courseId,
    s12nId,
  });

  const hasCompletedStandaloneCourseAndDoesNotHaveOwnership =
    !courseOwnership?.owns && isStandaloneCourse && hasCompletedCourse;
  const { userProductTransaction, loading } = useUserProductTransaction(
    tupleToStringKey([getUser().id, VERIFIED_CERTIFICATE, courseId]),
    !hasCompletedStandaloneCourseAndDoesNotHaveOwnership
  );
  const hasCompletedMultiModuleCourseInS12nAndDoesNotHaveOwnership =
    !courseOwnership?.owns && !isStandaloneCourse && numberOfModulesInCourse > 1 && hasCompletedCourse;
  const hasCompletedStandaloneCourseWithPaymentAndDoesNotHaveOwnership =
    hasCompletedStandaloneCourseAndDoesNotHaveOwnership && !loading && !userProductTransaction;

  return !!(
    isFreemiumLearner &&
    isFreemiumModelRolloutGAEnabled(courseId) &&
    (hasCompletedStandaloneCourseWithPaymentAndDoesNotHaveOwnership ||
      hasCompletedMultiModuleCourseInS12nAndDoesNotHaveOwnership)
  );
};

export const canEnrollThroughFreemium = ({
  courseId,
  enrollmentAvailableChoices,
  courseTypeMetadataWithVersion,
}: {
  courseId: string;
  enrollmentAvailableChoices: EnrollmentChoices;
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1;
}) => {
  if (!isFreemiumModelEnabled(courseId)) {
    return false;
  }

  const {
    canSubscribeToS12n,
    canPurchaseSingleCourse,
    canAuditCourse,
    hasFreeEnrollOptionIntoCourse,
    canPurchaseThroughCourseraPlus,
    canEnrollThroughS12nPrepaid,
  } = enrollmentAvailableChoices;

  const canAuditAndSubscribeToS12n = canSubscribeToS12n && canAuditCourse;
  const canAuditAndPurchaseCourse = canPurchaseSingleCourse && canAuditCourse;
  const canFreeEnrollAndPurchaseCourse = canPurchaseSingleCourse && hasFreeEnrollOptionIntoCourse;
  const canAuditAndEnrollThroughS12nPrepaid = canEnrollThroughS12nPrepaid && canAuditCourse;
  const canAuditAndPurchaseThroughCourseraPlus = canPurchaseThroughCourseraPlus && canAuditCourse;
  const isStandardCourse = courseTypeMetadataWithVersion?.courseTypeMetadata.typeName === CourseType.STANDARD_COURSE;

  return (
    isStandardCourse &&
    (canAuditAndSubscribeToS12n ||
      canAuditAndPurchaseCourse ||
      canFreeEnrollAndPurchaseCourse ||
      (isFreemiumModelRolloutGAEnabled(courseId) &&
        (canAuditAndEnrollThroughS12nPrepaid || canAuditAndPurchaseThroughCourseraPlus)))
  );
};

const FIRST_MODULE_COMPLETION_KEY = 'firstModuleCompletions';

/**
 * Sets or updates a value in localStorage to track that a learner has completed the first module in a course.
 * @param courseId - The ID of the course where the first module was completed
 */
export const setHasCompletedFirstModuleToLocalStorage = (courseId: string): void => {
  const userId = getUser()?.id;
  if (!userId || !courseId) {
    return;
  }

  const existingCompletions = localStorageEx.getItem(FIRST_MODULE_COMPLETION_KEY, JSON.parse, {});
  if (!existingCompletions[userId]) {
    existingCompletions[userId] = [courseId];
  } else if (!existingCompletions[userId].includes(courseId)) {
    existingCompletions[userId].push(courseId);
  }
  localStorageEx.setItem(FIRST_MODULE_COMPLETION_KEY, existingCompletions, JSON.stringify);
};

/**
 * Checks if a learner has completed the first module in a course.
 * @param courseId - The ID of the course to check
 * @returns boolean - True if the learner has completed the first module, false otherwise
 */
export const hasCompletedFirstModuleInLocalStorage = (courseId: string): boolean => {
  const userId = getUser()?.id;
  const existingCompletions = localStorageEx.getItem(FIRST_MODULE_COMPLETION_KEY, JSON.parse, {});
  return Boolean(existingCompletions[userId]?.includes(courseId));
};

type PropsFromNaptime = {
  openCourseMemberships?: OpenCourseMembershipsV1;
};

/**
 * Checks if a course has Coach available for Freemium purposes
 * TODO move this to out to another util once all Coach checks are consolidated to using this BE endpoint
 * @param courseId - The ID of the course to check
 * @returns {object} - Returns an object with the following properties:
 *   - `doesCourseHaveCoachAvailable`: boolean - True if the learner can access Coach for the course, false otherwise
 *   - `loading`: boolean - True if the API request is loading, false otherwise
 */
export const useDoesCourseHaveCoachAvailable = (courseId: string) => {
  const isFreemiumEnabled = courseId && isFreemiumModelEnabled(courseId);

  const { data, loading: loadingCoachEligibility } = useQuery<
    GetCoachAccessEligibilityQuery,
    GetCoachAccessEligibilityQueryVariables
  >(GetCoachAccessEligibility, {
    variables: {
      courseId,
    },
    skip: !isFreemiumEnabled,
    context: { clientName: 'gatewayGql' },
  });

  const doesCourseHaveCoachAvailable = data?.EnrollmentChoice?.queryCoachAccessEligibility?.isEligible ?? false;
  const loading = loadingCoachEligibility;

  return {
    doesCourseHaveCoachAvailable,
    loading: !isFreemiumEnabled ? false : loading,
  };
};

/**
 * Checks if a learner is eligible to access Coach for a specific course under Freemium
 * @param courseId - The ID of the course to check
 * @returns {object} - Returns an object with the following properties:
 *   - `isCourseEligibleForCoachAccess`: boolean - True if the learner has can access Coach for the course, false otherwise
 *   - `loading`: boolean - True if the API request is loading, false otherwise
 */
export const useIsCourseEligibleForCoachAccess = (courseId: string) => {
  const isFreemiumEnabled = courseId && isFreemiumModelEnabled(courseId);
  const auditUpgradeFlowContextData = useAuditUpgradeFlowData();

  const { doesCourseHaveCoachAvailable, loading: loadingCoachAvailable } = useDoesCourseHaveCoachAvailable(courseId);

  const {
    data: { openCourseMemberships },
    pending: loadingMembership,
  } = useNaptime<PropsFromNaptime>(() => {
    if (!isFreemiumEnabled || !doesCourseHaveCoachAvailable) {
      return {};
    }

    return {
      openCourseMemberships: OpenCourseMembershipsV1.get(tupleToStringKey([getUser().id, courseId])),
    };
  });

  const isFreemiumLearner = openCourseMemberships?.isFreemiumLearner ?? false;

  const { ownsCourse, loading: loadingCourseOwnership } = useCourseOwnership(courseId);

  const { hasCompletedFirstModule: _hasCompletedFirstModule, loading: loadingModuleCompletion } =
    useHasCompletedFirstModule({
      courseId,
      skip:
        !doesCourseHaveCoachAvailable ||
        (ownsCourse && !loadingCourseOwnership) ||
        !isFreemiumLearner ||
        !!auditUpgradeFlowContextData,
    });

  // Either retrieve from the audit upgrade flow context, which recomputes first module completion on every item view,
  // or retrieve directly from the hook
  const hasCompletedFirstModule = auditUpgradeFlowContextData?.hasCompletedFirstModule || _hasCompletedFirstModule;

  return {
    isCourseEligibleForCoachAccess: (!hasCompletedFirstModule || ownsCourse) && doesCourseHaveCoachAvailable,
    loading: loadingCoachAvailable || loadingMembership || loadingModuleCompletion || loadingCourseOwnership,
  };
};

export const useFreemiumTracking = (trackingName?: ButtonName) => {
  const track = useTracker();
  const pageData = useCoursePageData();
  const moduleOne = pageData?.material?.weeks?.[0];
  const moduleOneId = moduleOne?.id;
  const moduleOneName = moduleOne?.name;
  const courseId = pageData?.id;
  const courseSlug = pageData?.slug;
  const buttonName = trackingName || 'enroll_modal_freemium_preview_course_link';

  const trackPreviewLinkView = () => {
    if (!(courseId && courseSlug)) {
      return;
    }

    track('view_button', {
      button: {
        name: buttonName,
      },
      product: {
        id: courseId,
        slug: courseSlug,
      },
    });
  };

  const trackPreviewLinkClick = () => {
    if (!(moduleOneId && moduleOneName && courseId && courseSlug)) {
      return;
    }

    track('click_button', {
      button: {
        name: buttonName,
      },
      product: {
        id: courseId,
        slug: courseSlug,
      },
      courseBranchModule: {
        id: moduleOneId,
        moduleName: moduleOneName,
      },
    });
  };

  return {
    trackPreviewLinkView,
    trackPreviewLinkClick,
  };
};

export type ConfirmationData = {
  userId: number;
  hasSeenCourseraPlusUpsell?: boolean;
  hasCourseraPlusUpsell?: boolean;
  courseId: string;
  s12nId?: string;
  withFreeTrial?: boolean;
};

const CONFIRMATION_DATA_KEY = 'freemiumCheckoutConfirmation';

export const saveCheckoutConfirmationData = ({
  cart,
  hasCourseraPlusUpsell = false,
  hasSeenCourseraPlusUpsell = false,
}: {
  cart: CartsV2;
  hasCourseraPlusUpsell?: boolean;
  hasSeenCourseraPlusUpsell?: boolean;
}) => {
  let s12nId;
  let courseId;

  if (cart.isCourseraPlusSubscription) {
    s12nId = cart.getCourseraPlusS12nId();
    courseId = cart.getCourseraPlusCourseId();
  } else {
    s12nId = cart.getSpecializationIdFromCartItem(cart.firstCartItem);
    courseId = cart.isVC ? cart.productItemId : undefined;
  }

  const data = {
    userId: getUser().id,
    hasCourseraPlusUpsell: cart.isCourseraPlusSubscription || hasCourseraPlusUpsell,
    hasSeenCourseraPlusUpsell: isFreemiumModelRolloutGAEnabled()
      ? cart.isCourseraPlusSubscription || hasSeenCourseraPlusUpsell
      : false,
    courseId,
    s12nId,
    withFreeTrial: cart.hasFreeTrial,
  };
  store.set(CONFIRMATION_DATA_KEY, JSON.stringify(data));
};

export const clearCheckoutConfirmationData = () => {
  store.remove(CONFIRMATION_DATA_KEY);
};

export const getCheckoutConfirmationData = (): ConfirmationData | undefined => {
  const rawData = store.get(CONFIRMATION_DATA_KEY);
  return rawData ? JSON.parse(rawData) : undefined;
};

const NEXT_MODULE_POST_CHECKOUT_REDIRECT_URL_KEY = 'secondModulePostCheckoutRedirectUrl';

export const setSecondModulePostCheckoutRedirectUrl = (courseId: string, redirectUrl: string) => {
  const userId = getUser()?.id;
  if (!userId || !courseId || !redirectUrl) {
    return;
  }

  const existingData = localStorageEx.getItem(NEXT_MODULE_POST_CHECKOUT_REDIRECT_URL_KEY, JSON.parse, {});

  if (!existingData[userId]) {
    existingData[userId] = {};
  }

  existingData[userId][courseId] = redirectUrl;
  localStorageEx.setItem(NEXT_MODULE_POST_CHECKOUT_REDIRECT_URL_KEY, existingData, JSON.stringify);
};

export const getSecondModulePostCheckoutRedirectUrl = (courseId?: string): string | undefined => {
  const userId = getUser()?.id;
  if (!userId || !courseId) {
    return undefined;
  }

  const existingData = localStorageEx.getItem(NEXT_MODULE_POST_CHECKOUT_REDIRECT_URL_KEY, JSON.parse, {});
  return existingData[userId]?.[courseId];
};

export const clearSecondModulePostCheckoutRedirectUrl = (courseId: string) => {
  const userId = getUser()?.id;
  if (!userId || !courseId) {
    return;
  }

  const existingData = localStorageEx.getItem(NEXT_MODULE_POST_CHECKOUT_REDIRECT_URL_KEY, JSON.parse, {});
  if (existingData[userId]?.[courseId]) {
    delete existingData[userId][courseId];

    if (Object.keys(existingData[userId]).length === 0) {
      delete existingData[userId];
    }

    localStorageEx.setItem(NEXT_MODULE_POST_CHECKOUT_REDIRECT_URL_KEY, existingData, JSON.stringify);
  }
};

/**
 * Determines whether to show a locked icon for a freemium learner on a given module.
 * @param isFreemiumLearner - Whether the learner is enrolled through freemium access
 * @param moduleStatus - The completion status of the module
 * @param moduleNumber - The number of the module (1-based)
 * @returns boolean - True if the locked icon should be shown, false otherwise
 */
export const isFreemiumLocked = (props: {
  isFreemiumLearner: boolean | undefined;
  moduleStatus: string | undefined;
  moduleNumber: number;
}): boolean => {
  const { isFreemiumLearner, moduleStatus, moduleNumber } = props;
  return !!isFreemiumLearner && !!moduleStatus && moduleStatus !== 'COMPLETED' && moduleNumber !== 1;
};

// Same as the above, but use hooks to resolve isFreemiumLearner and moduleStatus
export const useIsFreemiumLocked = (props: {
  isFreemiumLearner?: boolean | undefined;
  moduleStatus?: string | undefined;
  moduleNumber: number;
}): boolean => {
  const auditUpgradeFlowData = useAuditUpgradeFlowData();
  const isFreemiumLearner = props.isFreemiumLearner || auditUpgradeFlowData?.isFreemiumLearner;

  const { navigationItems } = useCourseNavigationContext();
  const modules = navigationItems?.find((item) => item.typeName === 'weeksNavigationItem')?.definition.weeks;
  const currentModule = modules?.[props.moduleNumber - 1];
  const moduleStatus = props.moduleStatus || currentModule?.weekStatus;

  return isFreemiumLocked({ isFreemiumLearner, moduleStatus, moduleNumber: props.moduleNumber });
};

export const enrollThroughFreemium = async (courseId: string) => {
  const handleSubmitPromise = choiceTypeToHandleSubmitPromise[ENROLL_COURSE];
  const options = {
    courseId,
    isFreemiumLearner: true,
  };

  return submitEnrollmentPromise({ handleSubmitPromise, options });
};

export const generateImpressionForSurfacePreviewLink = (
  enrollmentAvailableChoices: EnrollmentChoices,
  courseId: string,
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1
) => {
  if (
    canEnrollThroughFreemium({
      courseId,
      enrollmentAvailableChoices,
      courseTypeMetadataWithVersion,
    })
  ) {
    googleCertificateExperiments.get('surfacePreviewLink');
  }
};

// for use in enroll section of the course page to preview the surface preview link
export const previewSurfacePreviewLink = (
  enrollmentAvailableChoices: EnrollmentChoices,
  courseId: string,
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1
) => {
  if (
    !canEnrollThroughFreemium({
      courseId,
      enrollmentAvailableChoices,
      courseTypeMetadataWithVersion,
    })
  ) {
    return 'control';
  }
  return googleCertificateExperiments.preview('surfacePreviewLink');
};

// For use in course page to preview the surface preview link
export const useSurfacePreviewLink = () => {
  const pageData = useCoursePageData();
  const courseId = pageData?.id;
  const { enrollmentAvailableChoices } = useEnrollment({ courseId });

  const {
    data: { courseTypeMetadataWithVersion },
  } = useNaptime<{
    courseTypeMetadataWithVersion?: CourseTypeMetadataV1Resource;
  }>(() => {
    if (!courseId) return {};

    return {
      courseTypeMetadataWithVersion: CourseTypeMetadataV1Resource.get(courseId),
    };
  }, [courseId]);

  const _previewSurfacePreviewLink = () => {
    if (!enrollmentAvailableChoices || !courseId) {
      return 'control';
    }

    return previewSurfacePreviewLink(enrollmentAvailableChoices, courseId, courseTypeMetadataWithVersion);
  };

  return {
    previewSurfacePreviewLink: _previewSurfacePreviewLink,
  };
};
